import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useOpenLoginModal } from '@/hooks/useOpenLoginModal';
import { useRouter } from 'next/router';
import { setBasicFilters } from '@/redux/store/actions/search';
import { fetchProforma, reserveHouse, ReserveParams } from '@/redux/store/actions/product';
import { getParsedDateByTimeStamp } from '@/helpers/props';
import { toast } from 'react-toastify';
import heap from '@/helpers/heap';
import useHouseAnalytics from '@/components/houses/_hooks/useHouseAnalytics';
import useProduct from '@/components/houses/_hooks/useProduct';
import { useMidTermQuery } from './useMidterm';
import getPlatform from '@/helpers/getPlatform';
import { ProductItemTypes } from '../_types/productItem.types';
import { formatPersianDates } from '@/helpers/dateManager';
const useHouse = (productState: ProductItemTypes) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const token = useSelector(state => state.token);
  const [loading, setLoading] = useState(false);
  const [displayGoftinoSection, setDisplayGoftinoSection] = useState<boolean>(true);
  const {
    calendarPricing
  } = useProduct();
  const {
    isMidTerm
  } = useMidTermQuery(productState);
  const {
    intrackHouseView
  } = useHouseAnalytics(isMidTerm);
  const isNonBookable = productState?.bookable_status === 'non_bookable';
  const {
    setLoginDrawerState
  } = useOpenLoginModal();
  const sortby = useRef<any>(router.query.sortby || 5);
  const {
    checkin,
    checkout,
    guests
  } = useSelector(state => state.basicFilters);
  const {
    data: bootstrapData
  } = useSelector(state => state.bootstrapData);
  const proforma = useSelector(state => state.proforma);
  const createReserveParams = () => {
    const sortTranslate = {
      7: 'is_descending[distance]=0',
      5: 'is_descending[rate]=1',
      4: 'is_descending[rank]=1',
      3: 'is_descending[price]=0',
      2: 'is_descending[price]=1'
    };
    const reserveParams: ReserveParams = {
      checkin: getParsedDateByTimeStamp(checkin),
      checkout: getParsedDateByTimeStamp(checkout),
      guests_count: guests,
      house_id: router.query.id,
      order_id: 'test',
      sort: sortTranslate[sortby.current],
      city_name: productState?.location?.city as string,
      ...(router?.query?.is_ad ? {
        from_plp_ads: '1'
      } : {})
      // ...(router?.query?.is_safe ? { is_safe: '1' } : {}),
    };
    return reserveParams;
  };
  const onSendReserveRequestClick = () => {
    // Check if data is not completed
    if (!checkin || !checkout || !guests) {
      toast.error('تاریخ و تعداد نفرات ضرروری می‌باشد', {
        position: 'bottom-left',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
      });
      return;
    }

    // Check if house is disabled
    // if (houseIsNonBookable()) {
    //   toast.error('این اقامتگاه غیر فعال می‌باشد', {
    //     position: 'top-right',
    //     autoClose: 5000,
    //     hideProgressBar: false,
    //     closeOnClick: true,
    //     pauseOnHover: true,
    //     draggable: true,
    //     progress: undefined,
    //   })
    //   return
    // }

    const handleSetSafarmarketImage = res => {
      const safarmarketId = router.query.safarmarketId;
      if (safarmarketId && !res?.data?.data?.is_instant) {
        const img = new Image();
        const duration = Math.floor((checkout - checkin) / (24 * 60 * 60));
        const checkinDate = new Date(checkin * 1000);
        const checkoutDate = new Date(checkout * 1000);
        const formatDate = (date: Date) => date.toISOString().split('T')[0];
        const extraPerson = proforma?.data?.details?.find(item => item?.key === 'extra_person')?.count || 0;
        const params = new URLSearchParams({
          pax: String(guests || 1),
          guests: String(Number(guests) - extraPerson || 1),
          extra: String(extraPerson),
          type: productState?.type_fa || '',
          ctyEn: productState?.location?.city_en || '',
          ctyFa: productState?.location?.city || '',
          state: productState?.location?.province || '',
          stayId: String(productState?.id || ''),
          sName: productState?.title || '',
          instant: res?.data?.data?.is_instant ? '1' : '0',
          totPr: String(proforma?.data?.total_payable_amount?.final_price || productState?.pricing.price_to_show?.amount || '0'),
          name: `${bootstrapData?.user_info?.name} ${bootstrapData?.user_info?.family}`,
          phone: String(bootstrapData?.user_info?.mobile),
          email: '',
          ci: formatDate(checkinDate),
          co: formatDate(checkoutDate),
          nights: String(duration || 1),
          orderNo: String(res?.data?.data?.reserve_id || '')
        });
        img.src = `https://safarmarket.com/api/stay/v1/pixel/shab/6/0/?smId=${safarmarketId}&${params.toString()}`;
        img.style.width = '1px';
        img.style.height = '1px';
        img.style.opacity = '0';
        document.body.appendChild(img);
      }
    };
    const redirectToInvoice = res => {
      handleSetSafarmarketImage(res);
      router.push(`/order-tracking/${res?.data?.data?.invoice_id}/show`);
    };
    const handleReserveWithLogin = () => {
      // Save user reserve action for login success usage
      sessionStorage.setItem('reserve_house', router.query.id as string);
      setLoginDrawerState({
        open: true,
        message: 'برای ادامه فرآیند رزرو و ثبت نهایی درخواست وارد حساب کاربری خود شوید.'
      });
    };

    // Check if user is logged in
    if (token) {
      // Handle Reserve
      dispatch(reserveHouse(createReserveParams(), setLoading, handleReserveWithLogin, redirectToInvoice, isMidTerm));
    } else {
      sessionStorage.setItem('reserve_house', router.query.id as string);
      handleReserveWithLogin();
    }
  };
  useEffect(() => {
    const convertedCheckin = Number(router.query.checkin) || checkin;
    const convertedCheckout = Number(router.query.checkout) || checkout;
    const convertedGuests = Number(router.query.guests) || guests;
    const basicfilter: any = {
      ...(convertedCheckin && {
        checkin: convertedCheckin
      }),
      ...(convertedCheckout && {
        checkout: convertedCheckout
      }),
      ...(convertedGuests && {
        guests: convertedGuests
      })
    };
    dispatch(setBasicFilters((convertedCheckin || convertedCheckout || convertedGuests) && basicfilter));
    if ((router.query.stclientId || token) && sessionStorage.getItem('reserve_house') === router.query.id && convertedCheckin && convertedCheckout) {
      sessionStorage.removeItem('reserve_house');
      onSendReserveRequestClick();
    } else {
      sessionStorage.removeItem('reserve_house');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.id, token]);
  const formatData = time => {
    const convertedDate = new Date(time * 1000);
    const firstDayFormatted = formatPersianDates(convertedDate);
    return `${firstDayFormatted.year}-${firstDayFormatted.month}-${firstDayFormatted.day}`;
  };
  useEffect(() => {
    if (process.env.OFFLINE_MODE === 'true') return;
    if (productState?.id && checkin && checkout) {
      heap.track('pre_invoice_view-pdp', {
        city_name: productState?.location?.city as string,
        house_id: router.query.id,
        ismidterm: isMidTerm,
        platform: getPlatform()
      });
      dispatch(fetchProforma({
        checkin: formatData(checkin),
        checkout: formatData(checkout),
        guestsCount: Number(guests) || 1,
        houseId: Number(productState?.id)
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkin, checkout, guests, productState.id]);
  useEffect(() => {
    intrackHouseView(productState);
    setDisplayGoftinoSection(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.id]);
  useEffect(() => {
    if (productState?.location?.city) {
      heap.track('view_house', {
        house_id: productState?.id,
        city_name: productState?.location?.city,
        ismidterm: isMidTerm
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, productState?.id, isMidTerm]);
  return {
    productState,
    calendarPricing,
    loading,
    isNonBookable,
    onSendReserveRequestClick: () => {
      heap.track('reserve_button_click', {
        house_id: router.query.id,
        city_name: productState?.location?.city,
        ismidterm: isMidTerm,
        platform: getPlatform(),
        is_login: !!token
      });
      onSendReserveRequestClick();
    },
    displayGoftinoSection,
    setDisplayGoftinoSection
  };
};
export default useHouse;